import { SiteStatusBadge } from 'components/Badges/SiteStatusBadge';
import { groupUnhealthyStatuses } from 'components/SiteSelector/Lists/groupUnhealthyStatuses';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationStatus } from 'types/Navigation';
import { PaginatedList } from 'types/PaginatedList';
import { useGet } from 'utils/hooks/useAPI';
import { StatusOrder } from 'utils/siteStatus';
import { useSiteSelectorContext } from '../SiteSelectorContext';
import { SiteGroup } from './SiteGroup';
import { SiteList } from './SiteList';
import { SiteSelectorNotice } from './SiteSelectorNotice';
import { Spinner } from 'shared/Spinner';

const sortStatusesAsc = (a: NavigationStatus, b: NavigationStatus) =>
    StatusOrder.indexOf(b.name) - StatusOrder.indexOf(a.name);

const sortStatusesDesc = (a: NavigationStatus, b: NavigationStatus) =>
    StatusOrder.indexOf(a.name) - StatusOrder.indexOf(b.name);

const NAVIGATION_STATUSES_API = '/helios-api/navigation/statuses';

export const StatusList = () => {
    const { t } = useTranslation('navigation', {
        keyPrefix: 'site_selector',
    });

    const { urlSearchParams, sortBy } = useSiteSelectorContext();

    const [statuses, setStatuses] = useState(
        new Map<string, NavigationStatus>()
    );

    const url = `${NAVIGATION_STATUSES_API}?${new URLSearchParams(
        urlSearchParams
    )}`;

    const previousUrlRef = useRef(url);

    const [{ data, error, loading }] =
        useGet<PaginatedList<NavigationStatus>>(url);

    useEffect(() => {
        if (data?.results) {
            const results = groupUnhealthyStatuses(data.results);
            setStatuses((currentStatuses) => {
                const nextStatuses = new Map(currentStatuses);

                results.forEach((result) =>
                    nextStatuses.set(result.name, result)
                );

                return nextStatuses;
            });
        }
    }, [data]);

    useEffect(() => {
        if (url !== previousUrlRef.current) {
            setStatuses(new Map());
        }

        previousUrlRef.current = url;
    }, [url]);

    if (!loading && error) return <SiteSelectorNotice message="no-sites" />;

    if (!loading && !statuses.size)
        return <SiteSelectorNotice message="service-error" />;

    if (loading && !error)
        return (
            <div className="w-full h-full flex flex-col items-center justify-start">
                <Spinner />
            </div>
        );

    return (
        <>
            {[...statuses.values()]
                .sort(
                    sortBy === 'alert_severity_asc'
                        ? sortStatusesAsc
                        : sortStatusesDesc
                )
                .map(({ name, sites, sites_count }) => (
                    <SiteGroup
                        key={name}
                        className="[&_button_>_svg]:mt-[3px]"
                        id={name}
                        sites_count={sites_count}
                        title={
                            <span className="flex gap-2 items-center lowercase">
                                {t('lists.status_list.status')}
                                <SiteStatusBadge status={name} tooltip />
                            </span>
                        }
                    >
                        <SiteList initialSites={sites} />
                    </SiteGroup>
                ))}
        </>
    );
};
